/**
 * NodeSelector — Popup centralizado de seleção de nodes.
 *
 * Abre ao double-click no canvas ou ao clicar no "+" de um node.
 * Fecha ao selecionar um preset, clicar fora, ou pressionar Escape.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { NODE_PRESETS, PRESET_CATEGORIES, type NodePreset } from '../presets/node-presets';

interface NodeSelectorProps {
  onSelect: (preset: NodePreset) => void;
  onClose: () => void;
  isSkillMode?: boolean;
  /** Se true, mostra apenas triggers (workflow ainda não tem trigger) */
  triggersOnly?: boolean;
}

export const NodeSelector: React.FC<NodeSelectorProps> = ({
  onSelect,
  onClose,
  isSkillMode = false,
  triggersOnly = false,
}) => {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Auto-focus search on open
  useEffect(() => {
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const filteredPresets = useMemo(() => {
    let presets = NODE_PRESETS;
    if (isSkillMode) {
      presets = presets.filter((p) => p.category !== 'trigger');
    }
    if (triggersOnly) {
      presets = presets.filter((p) => p.category === 'trigger');
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return presets.filter(
        (p) =>
          p.label.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q),
      );
    }
    return presets;
  }, [search, isSkillMode, triggersOnly]);

  const categories = useMemo(() => {
    if (search.trim()) return null;
    return PRESET_CATEGORIES.filter((cat) => {
      if (isSkillMode && cat.key === 'trigger') return false;
      if (triggersOnly && cat.key !== 'trigger') return false;
      return filteredPresets.some((p) => p.category === cat.key);
    });
  }, [search, isSkillMode, triggersOnly, filteredPresets]);

  const handleSelect = (preset: NodePreset) => {
    setSearch('');
    onSelect(preset);
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200]"
        style={{ backgroundColor: 'rgba(15,23,42,0.34)' }}
        onClick={onClose}
      />

      {/* Popup — force light theme */}
      <div
        ref={popupRef}
        data-testid="node-selector"
        className="fixed z-[201] flex flex-col"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 380,
          maxHeight: 540,
          backgroundColor: '#ffffff',
          borderRadius: 14,
          boxShadow: '0 18px 60px rgba(0,0,0,0.22)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-card-text': '#334155',
          '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
        } as React.CSSProperties}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            borderBottom: '1px solid #eef1f5',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
              {triggersOnly ? 'Selecione um gatilho para iniciar' : 'Adicionar node'}
            </div>
            {triggersOnly && (
              <div className="mt-1" style={{ fontSize: 11.5, color: '#94a3b8' }}>
                Todo workflow precisa de um gatilho como primeiro passo.
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid="node-selector-close"
            onClick={onClose}
            aria-label="Fechar"
            className="flex items-center justify-center rounded-lg transition-colors"
            style={{ width: 30, height: 30, color: '#64748b' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 16px 8px' }}>
          <div className="relative">
            <Icon
              name="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: '#94a3b8' }}
            />
            <input
              ref={searchRef}
              type="text"
              data-testid="node-selector-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar node..."
              className="w-full h-[38px] pl-10 pr-4 py-2.5 text-sm rounded-[10px] outline-none"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #d7dde5',
                color: '#1e293b',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.08)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#d7dde5'; e.currentTarget.style.boxShadow = 'none'; }}
            />
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '4px 8px 8px' }}>
          {categories ? (
            categories.map((cat) => (
              <div key={cat.key}>
                <div
                  className="px-2.5 pt-3 pb-1"
                  style={{ fontSize: 10.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  {cat.label}
                </div>
                {filteredPresets
                  .filter((p) => p.category === cat.key)
                  .map((preset) => (
                    <PresetItem key={preset.id} preset={preset} onClick={() => handleSelect(preset)} />
                  ))}
              </div>
            ))
          ) : (
            filteredPresets.map((preset) => (
              <PresetItem key={preset.id} preset={preset} onClick={() => handleSelect(preset)} />
            ))
          )}

          {filteredPresets.length === 0 && (
            <div className="text-center py-10 text-xs" style={{ color: '#94a3b8' }}>
              Nenhum node encontrado.
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

const PresetItem: React.FC<{ preset: NodePreset; onClick: () => void }> = ({ preset, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`node-selector-item-${preset.nodeType}`}
    className="flex items-center gap-2.5 w-full px-2.5 py-2 text-left rounded-[9px] transition-colors"
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <div
      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: preset.color }}
    >
      <Icon name={preset.icon} className="w-4 h-4" style={{ color: '#ffffff' }} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-semibold truncate" style={{ color: '#1e293b' }}>
        {preset.label}
      </div>
      <div className="text-[11px] truncate" style={{ color: '#94a3b8' }}>
        {preset.description}
      </div>
    </div>
  </button>
);

export default NodeSelector;
